// src/components/Analytics.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  collection,
  onSnapshot,
  doc,
  updateDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts';
import './Analytics.css';

// Mapeo de colores por etapa
const STAGE_COLORS = {
  éxito: '#2DD615',
  fracaso: '#F8130C',
  standby: '#3CB5FF',
};

// Mapeo de colores por causa y por etapa
const CAUSE_COLOR_MAP = {
  éxito: {
    'Agendó demostración': '#A8E6CF',
    'Interés en funcionalidades': '#56C596',
    'Cliente potencial': '#379683',
    'Identificación de necesidades': '#2E8B57',
    'Otro': '#B2E4C7',
  },
  fracaso: {
    'Lead malo': '#FF8B94',
    'Precios': '#FF6B6B',
    'Sin seguimiento efectivo': '#FF4E50',
    'Insatisfacción del cliente': '#FF2E2E',
    'Instagram': '#FF1B1C',
    'Envíos Masivos': '#FF7F7F',
    'Otro': '#FFB3B3',
  },
  standby: {
    'Sin información suficiente': '#A3D5FF',
    'Demo no agendada': '#7FBFFF',
    'Inconclusa': '#5FAAFF',
    'Otro': '#3F95FF',
  },
};

// Dueños que queremos mostrar en la tabla
const TABLE_OWNERS = [
  'Alejandro José',
  'Fabian Torres',
  'Fernando Daziano',
  'Sin asignar'
];

// Función para categorizar causas según etapa y texto
const categorizeCause = (stage, text) => {
  const t = text.toLowerCase();
  if (stage === 'éxito') {
    if (/\b(agend[óo]|agenda)[\s\w]*demo/.test(t)) return 'Agendó demostración';
    if (/\b(interés genuino|interes[óo] en funcionalidades)/.test(t)) return 'Interés en funcionalidades';
    if (/\b(cliente potencial|lead potencial)/.test(t)) return 'Cliente potencial';
    if (/\b(identific[oó] las necesidades?|detección de necesidades?)/.test(t)) return 'Identificación de necesidades';
    return 'Otro';
  }
  if (['fracaso','fracasos'].includes(stage)) {
    if (/\b(lead malo|mal lead)/.test(t)) return 'Lead malo';
    if (/\b(precio[s]?|costo[s]?)/.test(t)) return 'Precios';
    if (/\b(falta de seguimiento|sin seguimiento efectivo)/.test(t)) return 'Sin seguimiento efectivo';
    if (/\b(insatisfacción|frustraci[oó]n)/.test(t)) return 'Insatisfacción del cliente';
    if (/\binstagram\b/.test(t)) return 'Instagram';
    if (/\b(env[ií]os?\s+masivos?)/.test(t)) return 'Envíos Masivos';
    return 'Otro';
  }
  if (stage === 'standby') {
    if (/\b(sin información suficiente|insuficiente)/.test(t)) return 'Sin información suficiente';
    if (/\b(demo|agendar|programar)/.test(t) && !/\b(agendad[oa]|confirmad[oa])/.test(t)) return 'Demo no agendada';
    if (/\b(pendiente|standby|inconclusa)/.test(t)) return 'Inconclusa';
    return 'Otro';
  }
  return 'Otro';
};

const Analytics = () => {
  const assistantId = 'asst_fBFPIR6fGR1wQHVmNXydPPWv';
  const [threads, setThreads] = useState([]);

const [hourChartRange, setHourChartRange] = useState('');  // '', 'last7','last30','lastMonth','thisMonth','thisQuarter'
const [hourChartDate,  setHourChartDate]  = useState('');

// arriba, junto a tus useState para el chart de horas
const [weekdayChartRange, setWeekdayChartRange] = useState('last7')
const [weekdayChartFrom,  setWeekdayChartFrom]  = useState('')
const [weekdayChartTo,    setWeekdayChartTo]    = useState('')
// justo debajo de tus useState existentes
// para el filtro “Por fecha”
const [dateChartFrom, setDateChartFrom] = useState('')
const [dateChartTo,   setDateChartTo]   = useState('')



  // lista de fechas únicas disponibles
 const uniqueDates = useMemo(() => {
    const s = new Set(threads.map(t =>
      (new Date(t.createdAt || t.__createdAt))
        .toISOString().slice(0,10)
    ));
    return Array.from(s)
                .sort((a,b) => b.localeCompare(a));
  }, [threads]);

  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterStage, setFilterStage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // 1) Escucha en tiempo real
  useEffect(() => {
    const colRef = collection(db, `threads_${assistantId}`);
    const unsubscribe = onSnapshot(colRef, snap => {
      const docs = snap.docs
        .map(d => ({ id: d.id, ...d.data(), __createdAt: d.createTime?.toMillis() || 0 }))
        .sort((a,b) => b.__createdAt - a.__createdAt);
      setThreads(docs);
    }, console.error);
    return () => unsubscribe();
  }, [assistantId]);

  // 2) Computar estadísticas SOLO con análisis ya existentes
  useEffect(() => {
    const byOwner = {};
    threads.forEach(t => {
      const etiqueta = t.analisisconversacional?.etiqueta;
      const explicación = t.analisisconversacional?.explicación;
      if (!etiqueta) return; // omitimos hilos que no tengan análisis previo
      const owner = t.conversationowner || 'Sin asignar';
      (byOwner[owner] = byOwner[owner] || []).push({ hilo: t, etiqueta, explicación });
    });
    const result = {};
    Object.entries(byOwner).forEach(([owner, items]) => {
      result[owner] = {
        total: items.length,
        éxitos: items.filter(i => i.etiqueta === 'éxito').length,
        standbys: items.filter(i => i.etiqueta === 'standby').length,
        fracasos: items.filter(i => i.etiqueta === 'fracaso').length,
        detalles: items.map(i => ({
         threadId: i.hilo.id,
        senderName: i.hilo.senderName || 'Desconocido',
         etiqueta: i.etiqueta,
          explicación: i.explicación
        }))
      };
    });
    setStats(result);
    setLoading(false);
  }, [threads]);

  // 3) Filtrado global (estado + búsqueda)
  const filteredStats = useMemo(() => {
    const out = {};
    Object.entries(stats).forEach(([owner,data]) => {
      const detalles = data.detalles.filter(d => {
        const okStage = !filterStage || d.etiqueta===filterStage;
        const term = searchTerm.trim().toLowerCase();
        const okSearch = !term ||
          d.senderName.toLowerCase().includes(term) ||
          d.explicación.toLowerCase().includes(term);
        return okStage && okSearch;
      });
      if (detalles.length) {
        out[owner] = {
          total: detalles.length,
          éxitos: detalles.filter(d=>d.etiqueta==='éxito').length,
          standbys: detalles.filter(d=>d.etiqueta==='standby').length,
          fracasos: detalles.filter(d=>d.etiqueta==='fracaso').length,
          detalles
        };
      }
    });
    return out;
  }, [stats, filterStage, searchTerm]);

  // 4) Tabla resumida con acumulación de “otros” en “Sin asignar”
  const tableStats = useMemo(() => {
    const acc = {};
    // inicializo en cero
    TABLE_OWNERS.forEach(o => {
      acc[o] = { total:0, éxitos:0, standbys:0, fracasos:0 };
    });
    // agrego cada owner
    Object.entries(filteredStats).forEach(([owner,data]) => {
      const key = TABLE_OWNERS.includes(owner) ? owner : 'Sin asignar';
      acc[key].total   += data.total;
      acc[key].éxitos  += data.éxitos;
      acc[key].standbys+= data.standbys;
      acc[key].fracasos+= data.fracasos;
    });
    return acc;
  }, [filteredStats]);

  // 6) Totales generales
  const grandTotals = useMemo(() => {
    return TABLE_OWNERS.reduce((acc, owner) => {
      const s = tableStats[owner] || { total: 0, éxitos: 0, standbys: 0, fracasos: 0 };
      acc.total   += s.total;
      acc.éxitos  += s.éxitos;
      acc.standbys+= s.standbys;
      acc.fracasos+= s.fracasos;
      return acc;
    }, { total: 0, éxitos: 0, standbys: 0, fracasos: 0 });
  }, [tableStats]);

  // Para el filtro por rango de fecha
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const to = today.toISOString().slice(0,10);
    const fromDate = new Date();
    fromDate.setDate(today.getDate() - 6); // 7 días incluyendo hoy
    const from = fromDate.toISOString().slice(0,10);
    return { from, to };
  });

  // Filtra threads según rango de fecha
  const dateFilteredThreads = useMemo(() => {
    return threads.filter(t => {
      if (!dateRange.from && !dateRange.to) return true;
      const d = new Date(t.createdAt || t.__createdAt);
      if (dateRange.from && d < new Date(dateRange.from)) return false;
      if (dateRange.to   && d > new Date(dateRange.to))   return false;
      return true;
    });
  }, [threads, dateRange]);

  // 1) Conteo por hora
  const hourData = useMemo(() => {
    // función auxiliar para contar horas entre dos fechas
    const buildCounts = (from, to, divideByDays=false) => {
      const counts = Array(24).fill(0);
      const daySet = new Set();
      threads.forEach(t => {
        const d = new Date(t.createdAt || t.__createdAt);
        const iso = d.toISOString().slice(0,10);
        if (d >= from && d <= to) {
          counts[d.getHours()]++;
          if (divideByDays) daySet.add(iso);
        }
      });
      const divisor = divideByDays ? (daySet.size||1) : 1;
      return counts.map((c,h) => ({
        hour: h,
        count: +(c/divisor).toFixed(2),
        hourLabel: `${h.toString().padStart(2,'0')}hs`
      }));
    };

    const now = new Date();
    let start, end = now;

    if (hourChartRange) {
      switch(hourChartRange) {
        case 'last7':
          start = new Date(now); start.setDate(now.getDate() - 6);
          return buildCounts(start, end, true);
        case 'last30':
          start = new Date(now); start.setDate(now.getDate() - 29);
          return buildCounts(start, end, true);
        case 'lastMonth': {
          const ym = now.getFullYear(), m = now.getMonth();
          const pm = m===0 ? 11 : m-1;
          const py = m===0 ? ym-1 : ym;
          start = new Date(py, pm, 1);
          end = new Date(py, pm+1, 0);
          return buildCounts(start, end, true);
        }
        case 'thisMonth':
          start = new Date(now.getFullYear(), now.getMonth(), 1);
          return buildCounts(start, end, true);
        case 'thisQuarter': {
          const qStart = 3*Math.floor(now.getMonth()/3);
          start = new Date(now.getFullYear(), qStart, 1);
          return buildCounts(start, end, true);
        }
      }
    }
    if (hourChartDate) {
      const day = new Date(hourChartDate);
      return buildCounts(day, new Date(day.getFullYear(), day.getMonth(), day.getDate(),23,59,59));
    }
    // promedio todos los días
    const veryEarly = new Date(0);
    return buildCounts(veryEarly, end, true);
  }, [threads, hourChartRange, hourChartDate]);
  // 2) Conteo por día de la semana
  const weekdays = ['Domingo','Lunes','Martes','Miércoles','Jueves','Viernes','Sábado'];

  // 2) Conteo por fecha con etiqueta de día de la semana
// justo debajo de const weekdays = [...]
const weekdayData = useMemo(() => {
  // calcula start/end según rango o período
  const now = new Date()
  let start, end = now
  if (weekdayChartFrom && weekdayChartTo) {
    // período específico
    start = new Date(weekdayChartFrom)
    end   = new Date(`${weekdayChartTo}T23:59:59`)
  } else {
    // rango predefinido o por defecto last7
    switch (weekdayChartRange) {
      case 'last30':
        start = new Date(now); start.setDate(now.getDate() - 29)
        break
      case 'lastMonth': {
        const ym = now.getFullYear(), m = now.getMonth()
        const pm = m === 0 ? 11 : m - 1
        const py = m === 0 ? ym - 1 : ym
        start = new Date(py, pm, 1)
        end   = new Date(py, pm + 1, 0)
        break
      }
      case 'thisMonth':
        start = new Date(now.getFullYear(), now.getMonth(), 1)
        break
      case 'thisQuarter': {
        const qStart = 3 * Math.floor(now.getMonth() / 3)
        start = new Date(now.getFullYear(), qStart, 1)
        break
      }
      case 'last7':
      default:
        start = new Date(now); start.setDate(now.getDate() - 6)
    }
  }

  // agrupa y cuenta sólo los hilos en [start, end]
  const cnt = {}
  threads.forEach(t => {
    const d = new Date(t.createdAt || t.__createdAt)
    if (d < start || d > end) return
    const iso = d.toISOString().slice(0,10)
    const dayName = weekdays[d.getDay()]
    if (!cnt[iso]) cnt[iso] = { day: dayName, date: iso, count: 0 }
    cnt[iso].count++
  })

  return Object.values(cnt)
    .sort((a,b) => a.date.localeCompare(b.date))
}, [threads, weekdayChartRange, weekdayChartFrom, weekdayChartTo])

  // 3) Conteo por fecha
// justo debajo de weekdayData…
const dateData = useMemo(() => {
  const cnt = {}
  threads.forEach(t => {
    const d = new Date(t.createdAt || t.__createdAt)
    const iso = d.toISOString().slice(0,10)
    // si el usuario eligió un rango, filtramos
    if (dateChartFrom && iso < dateChartFrom) return
    if (dateChartTo   && iso > dateChartTo)   return
    cnt[iso] = (cnt[iso]||0) + 1
  })
  return Object.entries(cnt)
    .sort(([a],[b])=> a.localeCompare(b))
    .map(([date, count])=>({ date, count }))
}, [threads, dateChartFrom, dateChartTo])

  // 5) Preparar datos para gráficos
  const stages = ['éxito','standby','fracaso'];
  // Orden original de las tortas por causa (verde → rojo → azul)
 const causeStages = ['éxito','fracaso','standby'];
  const pieData = stages.map(stage => ({
    name: stage,
    value: Object.values(filteredStats)
      .reduce((sum,od)=> sum + od.detalles.filter(d=>d.etiqueta===stage).length, 0)
  }));
const pieCauseData = stage => {
  const m = {};
  Object.values(filteredStats).forEach(ownerData => {
    ownerData.detalles.forEach(item => {
      if (item.etiqueta === stage) {
        const cause = categorizeCause(item.etiqueta, item.explicación);
        m[cause] = (m[cause] || 0) + 1;
      }
    });
  });
  return Object.entries(m).map(([name, value]) => ({ name, value }));
};

  if (loading && !Object.keys(stats).length) {
    return <div className="loading">Cargando analíticas…</div>;
  }

  return (
    <div className="analytics-container">
      {loading && <div className="updating-banner">Actualizando análisis pendientes…</div>}
      <h2>📊 Analíticas de Conversaciones</h2>

   

      {/* Tabla resumen (solo los 4, con “otros” acumulados en Sin asignar) */}
      <table className="analytics-table">
        <thead>
          <tr>
            <th>Vendedor</th>
            <th>Total</th>
            <th>Éxitos</th>
            <th>Standby</th>
            <th>Fracasos</th>
          </tr>
        </thead>
        <tbody>
          {TABLE_OWNERS.map(owner => (
            <tr key={owner}>
              {/* Renombrar Sin asignar */}
              <td>
                {owner === 'Sin asignar'
                  ? 'Sin asignar (Dany)'
                  : owner}
              </td>
              <td>{tableStats[owner].total}</td>
              <td>{tableStats[owner].éxitos}</td>
              <td>{tableStats[owner].standbys}</td>
              <td>{tableStats[owner].fracasos}</td>
            </tr>
          ))}

          {/* Fila Total */}
          <tr className="total-row">
            <td><strong>Total</strong></td>
            <td><strong>{grandTotals.total}</strong></td>
            <td><strong>{grandTotals.éxitos}</strong></td>
            <td><strong>{grandTotals.standbys}</strong></td>
            <td><strong>{grandTotals.fracasos}</strong></td>
          </tr>
        </tbody>
      </table>

 {/* Gráfico general de etapas */}
      <div className="analytics-charts">
        <h3>Distribución de etapas</h3>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={100} label>
              {pieData.map((e,i)=>(
                <Cell key={i} fill={STAGE_COLORS[e.name]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>

    {/* Gráficos por causa y etapa */}
<div className="analytics-cause-charts">
  {causeStages.map(stage => (
    <div key={stage} style={{ width: '30%', display: 'inline-block', textAlign: 'center' }}>
      <h4>Causas de {stage.charAt(0).toUpperCase() + stage.slice(1)}</h4>
      <ResponsiveContainer width="100%" height={200}>
        <PieChart>
          <Pie
            data={pieCauseData(stage)}
            dataKey="value"
            nameKey="name"
            outerRadius={80}
            label
          >
            {pieCauseData(stage).map((entry, i) => (
              <Cell
                key={`cause-${stage}-${i}`}
                fill={CAUSE_COLOR_MAP[stage][entry.name] || '#CCCCCC'}
              />
            ))}
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
    </div>
  ))}
</div>

      <div className="analytics-extra-charts">

  {/* — Conversaciones por hora — */}
  <div className="chart-item">
    <h3>Conversaciones por hora</h3>

    {/* Aquí va tu filtro de rango + fecha */}
    <div className="chart-filter">
      <label>Rango:</label>
      <select
        value={hourChartRange}
        onChange={e => { setHourChartRange(e.target.value); setHourChartDate(''); }}
      >
        <option value="">Promedio todos los días</option>
        <option value="last7">Últimos 7 días</option>
        <option value="last30">Últimos 30 días</option>
        <option value="lastMonth">Mes pasado</option>
        <option value="thisMonth">Este mes</option>
        <option value="thisQuarter">Este trimestre</option>
      </select>
      <label>O fecha específica:</label>
      <select
        value={hourChartDate}
        onChange={e => { setHourChartDate(e.target.value); setHourChartRange(''); }}
      >
        <option value="">Ninguna</option>
        {uniqueDates.map(d => (
          <option key={d} value={d}>
            {d.split('-').reverse().join('/')}
          </option>
        ))}
      </select>
    </div>

    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={hourData} margin={{ top:5, right:20, left:0, bottom:30 }}>
        <XAxis dataKey="hourLabel" interval={0} />
        <YAxis />
        <Tooltip
          labelFormatter={label => label}
          formatter={(value) => {
            if (hourChartRange || !hourChartDate) {
              return [value, 'Promedio'];
            }
            return [value, 'Conversaciones'];
          }}
        />
        <Bar dataKey="count" fill="#3CB5FF" />
      </BarChart>
    </ResponsiveContainer>
  </div>

{/* Conversaciones por día de la semana */}
<div className="chart-item">
  <h3>Por día de la semana</h3>

  {/* — Filtro rango / período de fechas — */}
  <div className="chart-filter">
    <select
      value={weekdayChartRange}
      onChange={e => {
        setWeekdayChartRange(e.target.value)
        // al cambiar rango, limpiamos el período
        setWeekdayChartFrom('')
        setWeekdayChartTo('')
      }}
    >
      <option value="last7">Últimos 7 días</option>
    </select>

    <label>O semana especifica:</label>
    <input
      type="date"
      value={weekdayChartFrom}
      onChange={e => {
        setWeekdayChartFrom(e.target.value)
        setWeekdayChartRange('')
      }}
    />
    <span> a </span>
    <input
      type="date"
      value={weekdayChartTo}
      onChange={e => {
        setWeekdayChartTo(e.target.value)
        setWeekdayChartRange('')
      }}
    />
  </div>

  <ResponsiveContainer width="100%" height={200}>
    <BarChart
      data={weekdayData /* tu useMemo debe filtrar según weekdayChartRange o weekdayChartFrom/to */}
      margin={{ top:5, right:20, left:0, bottom:30 }}
    >
      <XAxis
        dataKey="day"
        angle={-45}
        textAnchor="end"
        interval={0}
        height={60}
      />
      <YAxis />

      {/* Tooltip siempre “Conversaciones:” */}
      <Tooltip
        formatter={value => [value, 'Conversaciones']}
        labelFormatter={(_, payload) => {
          if (!payload || !payload[0]) return ''
          const { day, date } = payload[0].payload  // día de la semana + fecha ISO
          const [yy, mm, dd] = date.split('-')
          return `${day} ${dd}/${mm}/${yy}`
        }}
      />

      <Bar dataKey="count" fill="#2DD615" />
    </BarChart>
  </ResponsiveContainer>
</div>

{/* Conversaciones por fecha */}
<div className="chart-item">
  <h3>Por fecha</h3>

  {/* — Filtro interno Por Fecha — */}
  <div className="chart-filter">
    <label>Desde:</label>
    <input
      type="date"
      value={dateChartFrom}
      onChange={e => setDateChartFrom(e.target.value)}
    />
    <label>Hasta:</label>
    <input
      type="date"
      value={dateChartTo}
      onChange={e => setDateChartTo(e.target.value)}
    />
    <button
      onClick={() => { setDateChartFrom(''); setDateChartTo(''); }}
    >
      Limpiar
    </button>
  </div>


  <ResponsiveContainer width="100%" height={200}>
    <LineChart data={dateData} margin={{ top:5, right:20, left:0, bottom:5 }}>
      <CartesianGrid strokeDasharray="3 3" />

      {/* Mostrar solo el mes/año en el eje X */}
      <XAxis
        dataKey="date"
        tickFormatter={dateStr => {
          const d = new Date(dateStr);
          // formatea "Mes Año", p.ej. "jul 2025"
          return d.toLocaleString('es', { month: 'short', year: 'numeric' });
        }}
      />

      <YAxis />

      <Tooltip
        // en la tooltip: mostrar siempre "Conversaciones"
        formatter={value => [value, 'Conversaciones']}
        // formatea la fecha como "23/07/2025"
        labelFormatter={dateStr => {
          const d = new Date(dateStr);
          const dd = String(d.getDate()).padStart(2, '0');
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const yyyy = d.getFullYear();
          return `${dd}/${mm}/${yyyy}`;
        }}
      />

      <Line type="monotone" dataKey="count" stroke="#F8130C" />
    </LineChart>
  </ResponsiveContainer>
</div>
</div>


     

   {/* Filtros */}
      <div className="analytics-filters">
        <label>
          Filtrar por estado:
          <select className='analytics-select' value={filterStage} onChange={e=>setFilterStage(e.target.value)}>
            <option value="">Todos</option>
            <option value="éxito">Éxitos</option>
            <option value="standby">Standby</option>
            <option value="fracaso">Fracasos</option>
          </select>
        </label>
        <label>
          Buscar:
          <input
          className='analytics-input'
            type="text"
            placeholder=""
            value={searchTerm}
            onChange={e=>setSearchTerm(e.target.value)}
          />
        </label>
      </div>

      {/* Detalle por hilo (solo propietarios visibles) */}
      <div className="analytics-details">
        {TABLE_OWNERS.filter(o=>'Sin asignar'!==o).map(owner=>{
          const data = filteredStats[owner] || {detalles:[]};
          return (
            <div key={owner} className="owner-block">
              <h3>{owner}</h3>
              <ul>
                {data.detalles.map(d=>(
                  <li key={d.threadId} className="detail-item">
                    <strong>{d.senderName}</strong> — <em>{d.etiqueta.toUpperCase()}</em><br/>
                    <small>{d.explicación}</small>
                    <a
                      className="chat-button"
                      href={`https://platform.thethingapp.com/messages/${assistantId}/${d.threadId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Ir al chat
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
        {/* También mostrar “Sin asignar” en detalle */}
        {filteredStats['Sin asignar'] && (
          <div className="owner-block">
            <h3>Sin asignar (Dany)</h3>
            <ul>
              {filteredStats['Sin asignar'].detalles.map(d=>(
                <li key={d.threadId} className="detail-item">
                  <strong>{d.senderName}</strong> — <em>{d.etiqueta.toUpperCase()}</em><br/>
                  <small>{d.explicación}</small>
                  <a
                    className="chat-button"
                    href={`https://platform.thethingapp.com/messages/${assistantId}/${d.threadId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Ir al chat
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default Analytics;
